import { useState } from 'react'
import { AlertTriangle, CalendarRange, Check, Loader2, Upload, X } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { travelDayLabel } from '../../lib/calendar'
import { anzahl, clock, duration, num } from '../../lib/format'
import { tracksByDay } from '../../lib/gpx'
import { activeStages } from '../../lib/trip'
import type { DiaryTrack } from '../../lib/types'
import { leseDateien, warnungenAus, type Lesefehler } from './mehrdateien'
import { anwenden, ersteZuordnung, neuBewertet, stageDate, type Zuordnung } from './verteilen'

/** Was über die gelesenen Dateien zu berichten ist, bis übernommen wird. */
interface Lesebericht {
  /** Die Dateien, aus denen die Liste unten entstanden ist. */
  dateien: string[]
  /** Je Datei ein Satz, wenn an ihr etwas nicht in Ordnung war. */
  warnungen: string[]
  /** Die Dateien, aus denen gar nichts geworden ist. */
  fehler: Lesefehler[]
  /** Punkte, die in den langen Pausen lagen, über alle Dateien. */
  pausen: number
  /** Unbrauchbare Punkte, über alle Dateien. */
  dropped: number
}

/**
 * Aufzeichnungen über mehrere Tage auf die Etappen verteilen.
 *
 * Der Columbus hält fünfzig Stunden durch und hat acht Gigabyte: er schreibt
 * die halbe Reise in eine einzige Datei, wenn ihn niemand daran hindert. Ohne
 * diesen Weg landet sie vollständig auf der Etappe, bei der man gerade steht -
 * mit den Kilometern von fünf Tagen in einer Zeile.
 *
 * Umgekehrt geht es genauso: das Garmin schreibt je Fahrt eine Datei, und ein
 * gewöhnlicher Arbeitstag ergibt vier davon. Deshalb nimmt diese Maske mehrere
 * Dateien auf einmal - gelesen wird jede für sich, zerschnitten wird jede für
 * sich, und die Stücke stehen danach in einer Liste, sortiert nach der Uhr und
 * nicht nach dem Dateinamen (nachFahrtzeit in lib/gpx.ts).
 *
 * Warum hier und nicht im Reiter Daten: dort sitzt der Import, aber der ist das
 * Gegenteil von diesem hier. Er ersetzt den gesamten Bestand aus einer
 * Sicherung; dieser legt etwas dazu. Zwei Knöpfe nebeneinander, von denen
 * einer alles überschreibt und der andere ergänzt, sind eine Falle. Der Track
 * gehört ins Tagebuch, das Datum einer Etappe steht im Tagebuch, und die
 * Etappe, die danach einen Track hat, steht in derselben Liste darunter - genau
 * so, wie der Sammelaufruf für die Strecken oben in der Etappenliste sitzt
 * (features/stages/StageRoutesPanel.tsx).
 *
 * Geschrieben wird nichts, bevor der Nutzer nicht gesehen hat, was wohin geht:
 * jede Zeile nennt ihren Tag, ihre Kilometer, ihre Etappe und was dort schon
 * liegt, jede lässt sich abwählen und jede auf eine andere Etappe legen. Ein
 * Fehlgriff träfe hier sechzehn Etappen auf einmal.
 */
export function TrackVerteilen() {
  const { trip, diary, setDiary } = useRoadbook()

  const [laedt, setLaedt] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  /** Was gelesen wurde, solange es noch nicht übernommen ist. */
  const [bericht, setBericht] = useState<Lesebericht | null>(null)
  const [zeilen, setZeilen] = useState<Zuordnung[]>([])

  const etappen = activeStages(trip)

  async function nimmDateien(liste: FileList | null) {
    if (!liste || liste.length === 0) return
    setLaedt(true)
    setMeldung(null)
    setBericht(null)
    setZeilen([])

    // Ein Zeitpunkt für die ganze Auswahl und nicht einer je Datei: "gelesen
    // am" meint diesen Knopfdruck, und bei Stücken ohne Uhr entscheidet er
    // ausserdem über die Reihenfolge - die soll die der Auswahl bleiben.
    const jetzt = Date.now()
    const { gelesen, fehler } = await leseDateien(liste, (xml, name) =>
      tracksByDay(xml, name, jetzt),
    )

    setBericht({
      dateien: gelesen.map((g) => g.name),
      warnungen: warnungenAus(gelesen),
      fehler,
      pausen: gelesen.reduce((sum, g) => sum + g.wert.pausen, 0),
      dropped: gelesen.reduce((sum, g) => sum + g.wert.dropped, 0),
    })
    setZeilen(
      ersteZuordnung(
        gelesen.flatMap((g) => g.wert.tage),
        trip,
        diary,
      ),
    )
    setLaedt(false)
  }

  function zuruecksetzen() {
    setBericht(null)
    setZeilen([])
  }

  /**
   * Setzt die Zieletappe einer Zeile neu.
   *
   * Die Beurteilung hängt an der Etappe und wird deshalb mitgerechnet: wer ein
   * Stück auf eine Etappe legt, auf der schon dieselbe Fahrt liegt, soll das
   * sehen, bevor er drückt - und nicht danach. Gerechnet wird dabei die ganze
   * Liste, nicht nur diese Zeile: ein Stück, das hier wegwandert, macht das
   * Stück, mit dem es sich beisst, anderswo unstrittig. Der Haken dieser einen
   * Zeile folgt derselben Regel wie beim ersten Vorschlag - gesetzt nur, was
   * unstrittig ist.
   */
  function setzeEtappe(index: number, stageId: string) {
    setZeilen((alt) =>
      neuBewertet(
        alt.map((z) => (z.index === index ? { ...z, stageId } : z)),
        diary,
        index,
      ),
    )
  }

  function setzeHaken(index: number, aktiv: boolean) {
    setZeilen((alt) =>
      neuBewertet(
        alt.map((z) => (z.index === index ? { ...z, aktiv } : z)),
        diary,
      ),
    )
  }

  function uebernehmen() {
    const auftraege = anwenden(zeilen, diary)
    if (auftraege.length === 0) return

    for (const auftrag of auftraege) setDiary(auftrag.stageId, { track: auftrag.track })

    const stuecke = zeilen.filter((z) => z.aktiv && z.stageId).length
    setMeldung(
      `${String(stuecke)} ${stuecke === 1 ? 'Stück' : 'Stücke'} auf ${String(auftraege.length)} ` +
        `${auftraege.length === 1 ? 'Etappe' : 'Etappen'} übernommen. Herausnehmen lässt sich jedes ` +
        'einzeln, unten bei der Aufzeichnung der jeweiligen Etappe.',
    )
    // Die Liste bezieht sich auf einen Bestand, den es so nicht mehr gibt:
    // stehen bliebe sie mit Beurteilungen von vorhin, und ein zweiter Druck
    // legte alles ein zweites Mal dazu.
    zuruecksetzen()
  }

  const gewaehlt = zeilen.filter((z) => z.aktiv && z.stageId).length

  return (
    <section className="strecken kein-druck">
      <h4 className="block-titel">
        <CalendarRange size={13} /> Aufzeichnung über mehrere Tage
      </h4>

      <p className="strecken-text">
        GPX-Dateien, die mehrere Fahrtage enthalten, werden an den langen Pausen zerschnitten und
        stückweise den Etappen mit dem passenden Datum zugeordnet. Mehrere Dateien lassen sich auf
        einmal wählen; sortiert werden sie nach der Uhr und nicht nach ihrem Namen. Übernommen wird
        nichts, bevor du die Liste gesehen hast.
      </p>

      <div className="knopfreihe">
        <label className="knopf still">
          {laedt ? <Loader2 size={14} className="dreht" /> : <Upload size={14} />}
          {laedt ? 'Dateien werden gelesen …' : 'GPX-Dateien wählen'}
          <input
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            multiple
            hidden
            onChange={(e) => {
              void nimmDateien(e.target.files)
              e.target.value = ''
            }}
          />
        </label>
        {bericht && (
          <button type="button" className="knopf still" onClick={zuruecksetzen}>
            <X size={14} /> Abbrechen
          </button>
        )}
      </div>

      {/*
        Die Dateien, aus denen nichts geworden ist, stehen einzeln da und mit
        Namen. Eine Sammelmeldung wäre hier wertlos: wer fünf Dateien gewählt
        hat, muss wissen, welche der fünf gemeint ist - und dass die anderen
        vier in der Liste darunter stehen.
      */}
      {bericht?.fehler.map((f, i) => (
        <p className="karte-warnung" key={i}>
          <AlertTriangle size={13} /> {f.name}: {f.grund}
        </p>
      ))}

      {bericht && zeilen.length > 0 && (
        <>
          <p className="strecken-text">
            {bericht.dateien.join(', ')} · {anzahl(zeilen.length, 'Fahrtag', 'Fahrtage')}
            {bericht.pausen > 0 &&
              ` · ${num(bericht.pausen)} Punkte lagen in den langen Pausen und gehören zu keinem Tag`}
            {bericht.dropped > 0 && ` · ${num(bericht.dropped)} unbrauchbare Punkte übersprungen`}
          </p>

          {/* Der Schlüssel ist die Stelle in der Liste: zweimal dieselbe Datei
              ergibt zweimal denselben Satz, und doppelte Schlüssel liessen den
              zweiten verschwinden. */}
          {bericht.warnungen.map((warnung, i) => (
            <p className="karte-warnung" key={i}>
              <AlertTriangle size={13} /> {warnung}
            </p>
          ))}

          <ol className="eintragsliste">
            {zeilen.map((z) => (
              <li className="eintrag" key={z.index}>
                <span className="eintrag-nummer">
                  {z.datum || 'ohne Datum'} · {stueckZeile(z.track)}
                </span>

                {/*
                  Der Dateiname steht nur da, wo mehr als eine Datei gewählt
                  wurde - dann aber muss er dastehen: bei vier Dateien eines
                  Tages ist er das Einzige, woran der Nutzer eine Zeile in der
                  Liste wiedererkennt, wenn er sie abwählen will.
                */}
                {bericht.dateien.length > 1 && (
                  <span className="strecken-text">{z.track.fileName || 'GPX-Datei'}</span>
                )}

                <div className="eintrag-zeile">
                  <label className="feld">
                    <span>Etappe</span>
                    <select
                      value={z.stageId}
                      onChange={(e) => {
                        setzeEtappe(z.index, e.target.value)
                      }}
                    >
                      <option value="">— keine, dieses Stück bleibt liegen —</option>
                      {etappen.map((s) => (
                        <option key={s.id} value={s.id}>
                          {/*
                            Die Zahl und das Datum dahinter kommen aus
                            derselben Rechnung: stageDate setzt den Reisetag
                            auf das Startdatum, also muss die Zahl davor der
                            Reisetag sein und nicht die Stelle in der Liste.
                          */}
                          {travelDayLabel(etappen, s)} {s.from} → {s.to} ·{' '}
                          {stageDate(s, trip, diary[s.id])}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="knopf still">
                    <input
                      type="checkbox"
                      checked={z.aktiv && z.stageId !== ''}
                      disabled={z.stageId === ''}
                      onChange={(e) => {
                        setzeHaken(z.index, e.target.checked)
                      }}
                    />
                    Übernehmen
                  </label>
                </div>

                <span className="strecken-text">{artSatz(z)}</span>
              </li>
            ))}
          </ol>

          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={gewaehlt === 0} onClick={uebernehmen}>
              <Check size={14} />
              {gewaehlt === 0
                ? 'Nichts ausgewählt'
                : `${String(gewaehlt)} ${gewaehlt === 1 ? 'Stück' : 'Stücke'} übernehmen`}
            </button>
          </div>
        </>
      )}

      {meldung && <p className="strecken-text">{meldung}</p>}

      <p className="abschluss">
        Geschnitten wird nicht um Mitternacht, sondern an den Pausen ab vier Stunden: ein Fahrtag
        hört dort auf, wo das Fahrzeug für die Nacht steht. Eine Fahrt, die um 23:40 endet, bleibt
        damit ganz. Das Stück selbst wird nicht verändert — die Kilometer, die Zeiten und die Halte
        jedes Tages sind über seine eigenen Rohpunkte gerechnet. Eine Datei, aus der nichts wird,
        kostet nur sich selbst: die übrigen stehen trotzdem in der Liste. Und was auf derselben
        Etappe landet, wird zusammengelegt und nicht zweimal gezählt — auch dann, wenn zwei der
        gewählten Dateien dieselbe Fahrt enthalten.
      </p>
    </section>
  )
}

/** Die Kennzahlen eines Stücks in einer Zeile. */
function stueckZeile(track: DiaryTrack): string {
  const zeit =
    track.startedAt !== null && track.endedAt !== null
      ? `${clock(track.startedAt)}–${clock(track.endedAt)}`
      : 'ohne Zeitstempel'
  const fahrt = track.movingSeconds > 0 ? `, ${duration(track.movingSeconds)} Fahrzeit` : ''
  return `${zeit} · ${num(track.distanceKm)} km${fahrt}`
}

/** Was mit dieser Zeile geschieht, in einem Satz. */
function artSatz(z: Zuordnung): string {
  switch (z.art) {
    case 'neu':
      return 'Diese Etappe hat noch keine Aufzeichnung.'
    case 'ergänzt':
      return 'Diese Etappe hat schon eine Aufzeichnung. Das Stück kommt dazu, zusammengelegt nach der Uhr.'
    // Das, womit sich eine Zeile beisst, kann im Tagebuch liegen oder weiter
    // oben in dieser Liste stehen - seit mehrere Dateien auf einmal gewählt
    // werden, ist der zweite Fall der häufigere. Beide Sätze nennen deshalb
    // beides: ein Satz, der nur vom Tagebuch spricht, schickte den Nutzer dort
    // suchen, wo nichts steht.
    case 'gleich':
      return 'Genau dieses Stück geht schon auf diese Etappe — aus dem Tagebuch oder aus einer der anderen gewählten Dateien. Angehakt zählen seine Kilometer ein zweites Mal.'
    case 'überlappt':
      return 'Der Zeitraum überschneidet sich mit etwas, das auf dieser Etappe schon liegt oder weiter oben in dieser Liste dorthin geht. Angehakt zählen Kilometer und Fahrzeit doppelt.'
    default:
      return z.datum
        ? `Keine Etappe mit dem Datum ${z.datum}. Wähle eine, wenn das Stück doch irgendwohin gehört.`
        : 'Ohne Zeitstempel lässt sich kein Tag bestimmen. Wähle die Etappe von Hand.'
  }
}
